<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header -->
    <div class="bg-white shadow">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <i class="fas fa-building text-indigo-600 text-3xl mr-3"></i>
            <div>
              <h1 class="text-2xl font-bold text-gray-900">Administration — Mandants</h1>
              <p class="text-sm text-gray-500 mt-1">
                Gestion des mandants : délai entre visites et planification
              </p>
            </div>
          </div>
          <div class="flex items-center gap-3">
            <span class="text-sm text-gray-500">
              {{ filteredMandators.length }} mandant{{ filteredMandators.length > 1 ? 's' : '' }}
            </span>
            <button
              @click="loadMandators"
              :disabled="loading"
              class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              <i :class="loading ? 'fas fa-spinner fa-spin' : 'fas fa-sync-alt'" class="mr-2"></i>
              Actualiser
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Main content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

      <!-- Filtres -->
      <div class="bg-white rounded-lg shadow mb-6 p-4">
        <div class="flex flex-col sm:flex-row gap-4">
          <!-- Recherche par nom -->
          <div class="flex-1">
            <label class="block text-xs font-medium text-gray-500 mb-1">Rechercher</label>
            <div class="relative">
              <i class="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Nom du mandant…"
                class="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
          <!-- Filtre par type -->
          <div class="sm:w-48">
            <label class="block text-xs font-medium text-gray-500 mb-1">Type</label>
            <select
              v-model="filterType"
              class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Tous les types</option>
              <option value="CAT">CAT (Assistance)</option>
              <option value="GPR">GPR (Gestion)</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-16">
        <i class="fas fa-spinner fa-spin text-indigo-600 text-4xl"></i>
        <p class="mt-4 text-gray-500">Chargement des mandants…</p>
      </div>

      <!-- Erreur -->
      <div v-else-if="loadError" class="bg-red-50 border border-red-200 rounded-lg p-4">
        <div class="flex items-start">
          <i class="fas fa-exclamation-circle text-red-400 text-xl mt-0.5 mr-3 flex-shrink-0"></i>
          <div>
            <h3 class="text-sm font-medium text-red-800">Erreur de chargement</h3>
            <p class="mt-1 text-sm text-red-700">{{ loadError }}</p>
          </div>
        </div>
      </div>

      <!-- Tableau des mandants -->
      <div v-else class="bg-white shadow rounded-lg overflow-hidden">
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Division</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                  Délai entre visites (mois)
                </th>
                <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-100">
              <tr v-if="filteredMandators.length === 0">
                <td colspan="5" class="px-4 py-8 text-center text-gray-400 text-sm">
                  Aucun mandant trouvé
                </td>
              </tr>
              <tr
                v-for="mandator in filteredMandators"
                :key="mandator.id"
                class="hover:bg-gray-50 transition-colors"
                :class="{ 'bg-indigo-50': editingId === mandator.id }"
              >
                <!-- Nom -->
                <td class="px-4 py-3">
                  <div class="text-sm font-medium text-gray-900">{{ mandator.name }}</div>
                  <div v-if="mandator.address && mandator.address.city" class="text-xs text-gray-400">
                    {{ mandator.address.zipCode }} {{ mandator.address.city }}
                  </div>
                </td>

                <!-- Type -->
                <td class="px-4 py-3">
                  <span
                    :class="{
                      'bg-blue-100 text-blue-800': mandator.type === 'CAT',
                      'bg-purple-100 text-purple-800': mandator.type === 'GPR',
                      'bg-gray-100 text-gray-600': mandator.type !== 'CAT' && mandator.type !== 'GPR',
                    }"
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                  >
                    {{ mandator.type }}
                  </span>
                </td>

                <!-- Division -->
                <td class="px-4 py-3 text-sm text-gray-600">
                  {{ mandator.division || '—' }}
                </td>

                <!-- Délai entre visites — édition inline -->
                <td class="px-4 py-3">
                  <div v-if="editingId === mandator.id" class="flex items-center gap-2">
                    <input
                      v-model.number="editingDelay"
                      type="number"
                      min="1"
                      max="120"
                      class="w-20 px-2 py-1 border border-indigo-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      @keyup.enter="saveDelay(mandator)"
                      @keyup.escape="cancelEdit"
                      ref="delayInput"
                    />
                    <span class="text-xs text-gray-400">mois</span>
                    <button
                      @click="saveDelay(mandator)"
                      :disabled="savingDelay"
                      class="text-green-600 hover:text-green-800 disabled:opacity-50"
                      title="Sauvegarder"
                    >
                      <i :class="savingDelay ? 'fas fa-spinner fa-spin' : 'fas fa-check'"></i>
                    </button>
                    <button
                      @click="cancelEdit"
                      class="text-gray-400 hover:text-gray-600"
                      title="Annuler"
                    >
                      <i class="fas fa-times"></i>
                    </button>
                  </div>
                  <div v-else class="flex items-center gap-2">
                    <span class="text-sm" :class="mandator.delayBetweenVisits ? 'text-gray-900' : 'text-gray-400'">
                      {{ mandator.delayBetweenVisits != null ? `${mandator.delayBetweenVisits} mois` : '—' }}
                    </span>
                    <button
                      @click="startEdit(mandator)"
                      class="text-indigo-400 hover:text-indigo-600"
                      title="Modifier le délai"
                    >
                      <i class="fas fa-pencil-alt text-xs"></i>
                    </button>
                  </div>
                </td>

                <!-- Actions -->
                <td class="px-4 py-3 text-right">
                  <button
                    @click="openUpdateModal(mandator)"
                    class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                    title="Mettre à jour les visites planifiées"
                  >
                    <i class="fas fa-calendar-alt mr-1.5"></i>
                    Mettre à jour les visites
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Notification flottante -->
      <transition name="fade">
        <div
          v-if="notification"
          class="fixed bottom-6 right-6 z-50 flex items-start gap-3 px-4 py-3 rounded-lg shadow-lg max-w-sm"
          :class="{
            'bg-green-50 border border-green-200': notification.type === 'success',
            'bg-red-50 border border-red-200': notification.type === 'error',
          }"
        >
          <i
            :class="{
              'fas fa-check-circle text-green-500': notification.type === 'success',
              'fas fa-times-circle text-red-500': notification.type === 'error',
            }"
            class="text-lg mt-0.5 flex-shrink-0"
          ></i>
          <p class="text-sm" :class="notification.type === 'success' ? 'text-green-800' : 'text-red-800'">
            {{ notification.message }}
          </p>
        </div>
      </transition>
    </div>

    <!-- ═══════════════════════════════════════════════════════════ -->
    <!-- Modal : mise à jour des visites planifiées                  -->
    <!-- ═══════════════════════════════════════════════════════════ -->
    <div
      v-if="showUpdateModal"
      class="fixed z-50 inset-0 overflow-y-auto"
      aria-modal="true"
      role="dialog"
    >
      <div class="flex items-center justify-center min-h-screen px-4 py-8">
        <!-- Overlay -->
        <div class="fixed inset-0 bg-gray-900 bg-opacity-50" @click="closeUpdateModal"></div>

        <!-- Dialogue -->
        <div class="relative bg-white rounded-xl shadow-2xl w-full max-w-lg z-10">
          <!-- En-tête -->
          <div class="flex items-start justify-between p-6 border-b border-gray-200">
            <div>
              <h3 class="text-lg font-semibold text-gray-900">
                Mettre à jour les visites planifiées
              </h3>
              <p class="text-sm text-gray-500 mt-1">
                Mandant : <strong>{{ selectedMandator && selectedMandator.name }}</strong>
              </p>
            </div>
            <button @click="closeUpdateModal" class="text-gray-400 hover:text-gray-600 ml-4">
              <i class="fas fa-times text-lg"></i>
            </button>
          </div>

          <!-- Corps -->
          <div class="p-6 space-y-6">

            <!-- Aperçu du nombre de propriétés affectées -->
            <div class="rounded-lg p-4"
              :class="{
                'bg-blue-50 border border-blue-200': previewCount !== null,
                'bg-gray-50 border border-gray-200': previewCount === null,
              }"
            >
              <div class="flex items-center gap-2">
                <i
                  :class="previewLoading ? 'fas fa-spinner fa-spin text-blue-500' : 'fas fa-info-circle text-blue-500'"
                  class="text-lg flex-shrink-0"
                ></i>
                <div>
                  <p v-if="previewLoading" class="text-sm text-gray-600">
                    Calcul du nombre de propriétés concernées…
                  </p>
                   <p v-else-if="previewCount !== null" class="text-sm text-blue-800">
                     <strong>{{ previewCount }} propriété{{ previewCount > 1 ? 's' : '' }}</strong>
                     ayant eu au moins une visite sera{{ previewCount > 1 ? 'ont' : '' }} affectée{{ previewCount > 1 ? 's' : '' }}
                     <span class="text-blue-600">(gestion en cours ou sans date de fin)</span>
                   </p>
                  <p v-else class="text-sm text-gray-500">Aperçu non disponible</p>
                </div>
              </div>
            </div>

            <!-- Sélection du mode de mise à jour -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-3">
                Mode de mise à jour de <code class="bg-gray-100 px-1 rounded text-xs">scheduled_visit_at</code>
              </label>
              <div class="space-y-3">
                <!-- Mode null_only -->
                <label
                  class="flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors"
                  :class="updateMode === 'null_only' ? 'border-indigo-400 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'"
                >
                  <input
                    v-model="updateMode"
                    type="radio"
                    value="null_only"
                    class="mt-0.5 text-indigo-600"
                  />
                  <div>
                    <p class="text-sm font-medium text-gray-900">
                      Calculer les dates manquantes
                      <span class="ml-2 text-xs font-normal text-gray-500">(défaut recommandé)</span>
                    </p>
                     <p class="text-xs text-gray-500 mt-0.5">
                       Affecte une date planifiée <em>uniquement</em> aux lots ayant eu une visite dont
                       <code class="bg-gray-100 px-1 rounded">scheduled_visit_at IS NULL</code>.
                       Les dates déjà définies ne sont pas modifiées.
                     </p>
                    <p
                      v-if="selectedMandator && selectedMandator.delayBetweenVisits == null"
                      class="mt-1 text-xs text-orange-700 font-medium"
                    >
                      ⚠ Ce mandant n'a pas de délai configuré — les propriétés sans délai ne pourront pas être calculées.
                    </p>
                  </div>
                </label>

                <!-- Mode Recalcul (toutes) -->
                <label
                  class="flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors"
                  :class="updateMode === 'recalculate' ? 'border-orange-400 bg-orange-50' : 'border-gray-200 hover:border-gray-300'"
                >
                  <input
                    v-model="updateMode"
                    type="radio"
                    value="recalculate"
                    class="mt-0.5 text-orange-600"
                  />
                  <div>
                    <p class="text-sm font-medium text-gray-900">
                      Recalculer toutes les dates
                      <span class="ml-2 text-xs font-normal text-orange-600">⚠ écrase les dates existantes</span>
                    </p>
                     <p class="text-xs text-gray-500 mt-0.5">
                       Recalcule <code class="bg-gray-100 px-1 rounded">dernière_visite + délai mois</code>
                       pour <strong>tous</strong> les lots ayant eu une visite, qu'ils aient ou non une date planifiée.
                     </p>
                  </div>
                </label>
              </div>
            </div>

            <!-- Sélection des bases de données cibles -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-3">
                Bases de données cibles
              </label>
              <div class="space-y-2">
                <label class="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:border-gray-300">
                  <input
                    v-model="targetMySQL"
                    type="checkbox"
                    class="text-indigo-600 rounded"
                  />
                  <div class="flex items-center gap-2">
                    <span class="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded">MySQL</span>
                    <span class="text-sm text-gray-700">Base de données opérationnelle</span>
                    <span class="text-xs text-gray-400">(source de vérité)</span>
                  </div>
                </label>
                <label class="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:border-gray-300">
                  <input
                    v-model="targetMSSQL"
                    type="checkbox"
                    class="text-indigo-600 rounded"
                  />
                  <div class="flex items-center gap-2">
                    <span class="bg-red-100 text-red-800 text-xs font-medium px-2 py-0.5 rounded">MSSQL</span>
                    <span class="text-sm text-gray-700">Base de données ALTAIX / source</span>
                  </div>
                </label>
              </div>
              <p v-if="!targetMySQL && !targetMSSQL" class="mt-2 text-xs text-red-600">
                Veuillez sélectionner au moins une base de données.
              </p>
            </div>

          </div>

          <!-- Pied -->
          <div class="px-6 py-4 bg-gray-50 rounded-b-xl flex items-center justify-between gap-3">
            <button
              @click="closeUpdateModal"
              class="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              @click="confirmUpdateVisits"
              :disabled="applying || !targetMySQL && !targetMSSQL"
              class="inline-flex items-center px-5 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i :class="applying ? 'fas fa-spinner fa-spin' : 'fas fa-check'" class="mr-2"></i>
              {{ applying ? 'Application en cours…' : 'Confirmer la mise à jour' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'

export default {
  name: 'AdminMandators',

  data() {
    return {
      // Liste
      mandators: [],
      loading: false,
      loadError: null,

      // Filtres
      searchQuery: '',
      filterType: '',

      // Édition inline du délai
      editingId: null,
      editingDelay: null,
      savingDelay: false,

      // Modal mise à jour visites
      showUpdateModal: false,
      selectedMandator: null,
      updateMode: 'null_only',
      targetMySQL: true,
      targetMSSQL: false,
      previewLoading: false,
      previewCount: null,
      applying: false,

      // Notification
      notification: null,
      notificationTimer: null,
    }
  },

  computed: {
    filteredMandators() {
      let list = this.mandators

      if (this.filterType) {
        list = list.filter(m => m.type === this.filterType)
      }

      if (this.searchQuery.trim()) {
        const q = this.searchQuery.trim().toLowerCase()
        list = list.filter(m => m.name && m.name.toLowerCase().includes(q))
      }

      return list
    },
  },

  mounted() {
    this.loadMandators()
  },

  methods: {
    // ── Chargement ────────────────────────────────────────────────────────────

    async loadMandators() {
      this.loading = true
      this.loadError = null
      try {
        this.mandators = await api.getMandators()
      } catch (error) {
        console.error('Erreur chargement mandants:', error)
        this.loadError = error.message || 'Impossible de charger les mandants.'
      } finally {
        this.loading = false
      }
    },

    // ── Édition inline du délai ───────────────────────────────────────────────

    startEdit(mandator) {
      this.editingId = mandator.id
      this.editingDelay = mandator.delayBetweenVisits
      this.$nextTick(() => {
        const input = this.$refs.delayInput
        if (input) {
          const el = Array.isArray(input) ? input[0] : input
          if (el) el.focus()
        }
      })
    },

    cancelEdit() {
      this.editingId = null
      this.editingDelay = null
    },

    async saveDelay(mandator) {
      if (this.savingDelay) return

      const delay = this.editingDelay === '' || this.editingDelay == null ? null : Number(this.editingDelay)

      if (delay !== null && (isNaN(delay) || delay < 1 || delay > 120)) {
        this.showNotification('error', 'Le délai doit être un entier entre 1 et 120 mois.')
        return
      }

      this.savingDelay = true
      try {
        await api.updateMandatorDelay(mandator.id, delay)

        // Mettre à jour localement
        const idx = this.mandators.findIndex(m => m.id === mandator.id)
        if (idx !== -1) {
          this.mandators[idx] = { ...this.mandators[idx], delayBetweenVisits: delay }
        }

        this.cancelEdit()
        this.showNotification('success', `Délai mis à jour : ${delay != null ? delay + ' mois' : 'aucun'}`)
      } catch (error) {
        console.error('Erreur sauvegarde délai:', error)
        this.showNotification('error', error.message || 'Erreur lors de la sauvegarde.')
      } finally {
        this.savingDelay = false
      }
    },

    // ── Modal mise à jour visites ─────────────────────────────────────────────

    async openUpdateModal(mandator) {
      this.selectedMandator = mandator
      this.updateMode = 'null_only'
      this.targetMySQL = true
      this.targetMSSQL = false
      this.previewCount = null
      this.applying = false
      this.showUpdateModal = true

      // Charger le preview
      await this.loadPreview(mandator.id)
    },

    closeUpdateModal() {
      if (this.applying) return
      this.showUpdateModal = false
      this.selectedMandator = null
      this.previewCount = null
    },

    async loadPreview(mandatorId) {
      this.previewLoading = true
      try {
        const result = await api.previewUpdateScheduledVisits(mandatorId)
        this.previewCount = result.affectedCount
      } catch (error) {
        console.error('Erreur preview:', error)
        this.previewCount = null
      } finally {
        this.previewLoading = false
      }
    },

    async confirmUpdateVisits() {
      if (!this.targetMySQL && !this.targetMSSQL) {
        this.showNotification('error', 'Veuillez sélectionner au moins une base de données.')
        return
      }

      this.applying = true
      try {
        const targets = []
        if (this.targetMySQL) targets.push('mysql')
        if (this.targetMSSQL) targets.push('mssql')

        const result = await api.applyUpdateScheduledVisits(
          this.selectedMandator.id,
          this.updateMode,
          targets
        )

        // Construire le message résumé
        const parts = []
        if (result.results?.mysql) {
          const r = result.results.mysql
          if (r.success) {
            parts.push(`MySQL : ${r.rowsAffected} ligne${r.rowsAffected > 1 ? 's' : ''} mise${r.rowsAffected > 1 ? 's' : ''} à jour`)
          } else {
            parts.push(`MySQL : erreur — ${r.error}`)
          }
        }
        if (result.results?.mssql) {
          const r = result.results.mssql
          if (r.success) {
            parts.push(`MSSQL : ${r.rowsAffected} ligne${r.rowsAffected > 1 ? 's' : ''} mise${r.rowsAffected > 1 ? 's' : ''} à jour`)
          } else {
            parts.push(`MSSQL : erreur — ${r.error}`)
          }
        }

        const type = result.success ? 'success' : 'error'
        this.showNotification(type, parts.join(' · ') || (result.success ? 'Mise à jour effectuée.' : 'Une erreur est survenue.'))

        this.closeUpdateModal()
      } catch (error) {
        console.error('Erreur application update:', error)
        this.showNotification('error', error.message || 'Erreur lors de la mise à jour.')
      } finally {
        this.applying = false
      }
    },

    // ── Notifications ─────────────────────────────────────────────────────────

    showNotification(type, message) {
      if (this.notificationTimer) clearTimeout(this.notificationTimer)
      this.notification = { type, message }
      this.notificationTimer = setTimeout(() => {
        this.notification = null
      }, 5000)
    },
  },
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(8px);
}
</style>
